/**
 * collectionsApi (feature 0023 P1) — the builder's typed READ client for the seller's collections
 * (backend "category"), used to populate the `collection-picker` field. Reuses the authed `apiRequest`
 * (Bearer from the JWT; `store_id` is derived server-side — no tenant surface). Read-only.
 *
 * Only ACTIVE collections are selectable: the admin list endpoint returns DRAFT collections too, but a
 * DRAFT binding would resolve to nothing on the storefront (P2 posts no resolved key) and the section
 * would silently vanish, so we filter to ACTIVE here (spec §5.2 / m6).
 */
import type { StorefrontCollection, StorefrontProduct } from '@aquilixshop/ui-react';
import { apiRequest } from './http';
import type { PickerItem, PickerSource } from '../inspector/controls/EntityPickerListbox';
import { createCachedPickerSource } from './pickerCache';

interface CollectionListItemDto {
  id: string;
  name: string;
  slug: string;
  status: string;
  type: string;
  coverImageUrl: string | null;
  productCount: number;
}
interface CollectionListResponse {
  items: CollectionListItemDto[];
  nextCursor: string | null;
}
interface CollectionResponse {
  id: string;
  name: string;
  slug: string;
  status: string;
  description?: string | null;
  coverImage: { url: string; alt: string | null } | null;
  productCount: number;
}

const PATH = '/api/collections';
const PICKER_LIMIT = '50';

function itemLabel(count: number): string {
  return count === 1 ? '1 product' : `${count} products`;
}

/** GET /api/collections?search=&limit= — the seller's ACTIVE collections matching `term`, for the picker. */
async function searchCollections(term: string): Promise<PickerItem[]> {
  const query: Record<string, string> = { limit: PICKER_LIMIT };
  if (term) query.search = term;
  const res = await apiRequest<CollectionListResponse>(PATH, { query });
  return res.items
    .filter((c) => c.status === 'ACTIVE')
    .map((c) => ({
      id: c.id,
      name: c.name,
      thumbnailUrl: c.coverImageUrl,
      meta: itemLabel(c.productCount),
    }));
}

/** GET /api/collections/{id} — the raw single-collection fetch (name/cover) the picker cache falls back to. */
async function fetchCollection(id: string): Promise<PickerItem | null> {
  const c = await apiRequest<CollectionResponse>(`${PATH}/${encodeURIComponent(id)}`);
  return {
    id: c.id,
    name: c.name,
    thumbnailUrl: c.coverImage?.url ?? null,
    meta: itemLabel(c.productCount),
  };
}

/** The storefront cap on a store's collections (`MAX_STOREFRONT_COLLECTIONS` back-side). */
const STOREFRONT_COLLECTION_CAP = 50;
/** At most this many list round-trips while skipping DRAFT rows — a bound, not an expectation. */
const MAX_LIST_PAGES = 4;

function toStorefront(c: {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  coverImageUrl?: string | null;
  coverImage?: { url: string; alt: string | null } | null;
  productCount: number;
}): StorefrontCollection {
  const url = c.coverImage?.url ?? c.coverImageUrl ?? null;
  const alt = c.coverImage?.alt ?? null;
  return {
    id: c.id,
    name: c.name,
    slug: c.slug,
    description: c.description ?? '',
    coverImage: url ? { url, alt: alt ?? c.name } : null,
    productCount: c.productCount,
  };
}

/**
 * The store's ACTIVE collections in the SAME order the storefront serves them, for the edit canvas.
 *
 * `collection-list` with `source: 'all'` reads `resolved.storeCollections`, which the page bundle fills
 * server-side (`getPublishedCollections` — ACTIVE only, `ORDER BY created_at DESC, id DESC`, capped at 50).
 * The builder had no equivalent, so that section painted DEMO collections ("Nouveautés / Meilleures ventes /
 * Promotions") in the editor no matter what the store actually held — and on a store with no collection it
 * promised a grid the customer never got. This is the editor half of that read.
 *
 * The admin list endpoint returns DRAFT rows too and pages by keyset in the same order, so we filter to
 * ACTIVE and follow the cursor until the storefront's cap is reached (bounded by {@link MAX_LIST_PAGES} —
 * a store that is all drafts must not spin). Same order in, same order out: no client-side re-sort.
 */
export async function listStoreCollections(): Promise<StorefrontCollection[]> {
  const out: StorefrontCollection[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < MAX_LIST_PAGES && out.length < STOREFRONT_COLLECTION_CAP; page += 1) {
    const query: Record<string, string> = { limit: PICKER_LIMIT };
    if (cursor) query.cursor = cursor;
    const res: CollectionListResponse = await apiRequest<CollectionListResponse>(PATH, { query });
    for (const c of res.items) if (c.status === 'ACTIVE') out.push(toStorefront(c));
    cursor = res.nextCursor;
    if (!cursor) break;
  }
  return out.slice(0, STOREFRONT_COLLECTION_CAP);
}

/**
 * GET /api/collections/{id} in the shape a section renders, or `null` when it would not resolve on the
 * live storefront. Mirrors `usePickedProducts`: a DRAFT/deleted pick resolves to nothing for the customer,
 * so resolving it in the editor would promise a card that never ships.
 */
export async function getStorefrontCollection(id: string): Promise<StorefrontCollection | null> {
  const c = await apiRequest<CollectionResponse>(`${PATH}/${encodeURIComponent(id)}`);
  return c.status === 'ACTIVE' ? toStorefront(c) : null;
}

/**
 * One product card exactly as the STOREFRONT serves it (backend `StorefrontProductView`). Deliberately a
 * different shape from the admin `ProductResponse` in `catalogApi`: this is the customer-facing projection —
 * no cost, no supplier, no dashboard-only photo, no raw stock — and grids carry OPTIONS but no variant list
 * (the sections derive display variants from options).
 */
interface StorefrontProductDto {
  id: string;
  name: string;
  slug: string;
  description: string;
  price: { amount: number; currency: string };
  compareAtPrice: { amount: number; currency: string } | null;
  images: { url: string; alt: string; isCover: boolean; optionValue?: string | null }[];
  /** The full gallery (photos AND clips, seller's order) — present since 20/09/2026, absent on older backends. */
  media?: {
    url: string;
    alt: string;
    kind: 'image' | 'video';
    posterUrl?: string | null;
    optionValue?: string | null;
    sources?: { url: string; width: number | null; height: number | null }[] | null;
  }[];
  options: {
    name: string;
    values: { value: string; swatch: string | null; imageUrl?: string | null }[];
  }[];
  seo: { metaTitle: string | null; metaDescription: string | null };
  tags?: string[];
  defaultVariantId?: string | null;
}

/**
 * GET /api/collections/{id}/storefront-products — the collection's products AS THE SHOP RENDERS THEM: ACTIVE
 * members only, the collection's MANUAL order, the same cap (12) the page bundle applies.
 *
 * Why a dedicated endpoint rather than `GET /api/products?collection=…`: the seller-side list sorts
 * newest-first, not by the collection's manual order, and carries admin-only fields. The canvas would have
 * painted a grid in an order the customer never sees. This read is the backend's own
 * `getPublishedCollectionProducts` — one definition of "a featured collection's products", shared with the
 * page bundle.
 *
 * Maps to the render `StorefrontProduct` the same way the storefront does (`storefront-api`'s
 * `productResponseToStorefront`) — `status:'active'` is implied by the projection (nothing else is served),
 * and `inventory` is not exposed to a card.
 */
export async function getCollectionStorefrontProducts(id: string): Promise<StorefrontProduct[]> {
  const rows = await apiRequest<StorefrontProductDto[]>(
    `${PATH}/${encodeURIComponent(id)}/storefront-products`,
  );
  return rows.map((p) => ({
    id: p.id,
    name: p.name,
    slug: p.slug,
    description: p.description,
    status: 'active',
    price: { amount: p.price.amount, currency: p.price.currency },
    compareAtPrice: p.compareAtPrice
      ? { amount: p.compareAtPrice.amount, currency: p.compareAtPrice.currency }
      : null,
    images: (p.images ?? []).map((i) => ({
      url: i.url,
      alt: i.alt,
      isCover: i.isCover,
      optionValue: i.optionValue ?? null,
    })),
    media: (p.media ?? []).map((m) => ({
      url: m.url,
      alt: m.alt,
      kind: m.kind === 'video' ? ('video' as const) : ('image' as const),
      posterUrl: m.posterUrl ?? null,
      optionValue: m.optionValue ?? null,
      sources: (m.sources ?? []).map((r) => ({ url: r.url, width: r.width, height: r.height })),
    })),
    inventory: { tracked: false, quantity: null },
    seo: { metaTitle: p.seo.metaTitle, metaDescription: p.seo.metaDescription },
    tags: p.tags ?? [],
    options: (p.options ?? []).map((o) => ({
      name: o.name,
      values: o.values.map((v) => ({
        label: v.value,
        swatch: v.swatch,
        imageUrl: v.imageUrl ?? null,
      })),
    })),
    variants: [],
    defaultVariantId: p.defaultVariantId ?? null,
  }));
}

/**
 * Stable (module-level) picker source for `CollectionControl`, wrapped in the shared client cache so the
 * pickers a section like collection-list renders SHARE one request, reopen instantly, and resolve a
 * trigger label from the loaded list instead of a per-collection GET. See {@link createCachedPickerSource}.
 */
export const collectionPickerSource: PickerSource = createCachedPickerSource({
  search: searchCollections,
  fetchOne: fetchCollection,
});
